// Product page quantity stepper: keeps the stepped/typed quantity honest against real stock.
//
// The number that matters is "how many more can be added right now": the input's own `max`
// (assets/product-info.js's setQuantityBoundries writes it as stock minus what's already in the
// cart). This file makes the field obey that ceiling — and the floor — however the value gets
// there:
//   * digits only (no minus, plus, "e", decimal separators);
//   * a value above the ceiling is pulled back down to it immediately, with a short explanation
//     ("Due to availability...") instead of silently letting Add to Cart fail;
//   * empty / 0 / below the minimum snaps back to the minimum when the field is left;
//   * nothing left to add (every unit already in the cart): the field locks at 0, Add to Cart is
//     disabled, and the message says why.
//
// Message text lives in data-msg-* attributes on the [data-qty-availability-error] element,
// rendered server-side in sections/main-product.liquid ({n} = most that can be added now, {c} =
// already in cart) — this file only fills the numbers in and flips `hidden`.
//
// Delegated at document level throughout, not bound to specific elements on load: product-info.js
// replaces the whole variant-selects/quantity markup on every color/size change (see
// handleUpdateProductInfo in assets/product-info.js), so anything bound directly to today's DOM
// nodes would go dead after the first variant swap.
(function () {
  function getRefs(input) {
    var scope = input.closest('product-info') || document;
    return {
      errorEl: scope.querySelector('[data-qty-availability-error]'),
      submitButton: scope.querySelector('.product-form__submit'),
    };
  }

  function fill(template, n, c) {
    return (template || '').replace(/\{n\}/g, n).replace(/\{c\}/g, c);
  }

  function showMessage(refs, key, n, c) {
    if (!refs.errorEl) return;
    var template = refs.errorEl.getAttribute('data-msg-' + key);
    if (!template) return;
    refs.errorEl.textContent = fill(template, n, c);
    refs.errorEl.hidden = false;
  }

  function hideMessage(refs) {
    if (refs.errorEl) refs.errorEl.hidden = true;
  }

  function limits(input) {
    var min = input.min !== '' ? parseInt(input.min, 10) : 1;
    var max = input.max !== '' ? parseInt(input.max, 10) : null;
    if (isNaN(min)) min = 1;
    if (max !== null && isNaN(max)) max = null;
    return { min: min, max: max, cart: parseInt(input.dataset.cartQuantity, 10) || 0 };
  }

  function sanitizeDigits(input) {
    var cleaned = input.value.replace(/[^0-9]/g, '');
    if (cleaned !== input.value) input.value = cleaned;
  }

  // Add to Cart is only ever disabled/re-enabled here for a reason *we* set (no stock left to add)
  // — toggleSubmitButton (assets/product-form.js) independently disables it for sold-out/loading,
  // and blindly re-enabling on every valid quantity would fight that.
  function setSubmitBlocked(refs, blocked) {
    var button = refs.submitButton;
    if (!button) return;
    if (blocked) {
      button.setAttribute('disabled', 'disabled');
      button.dataset.qtyBlocked = 'true';
    } else if (button.dataset.qtyBlocked === 'true') {
      button.removeAttribute('disabled');
      delete button.dataset.qtyBlocked;
    }
  }

  // `final` is true when the shopper is done typing (blur / Enter / stepper / variant change):
  // only then are empty, 0 and below-minimum values snapped up, since mid-typing they're just
  // steps on the way to a real number ("1" on the way to "12").
  function validate(input, final, quiet) {
    validateInner(input, final, quiet);
    // Keep the +/- buttons' disabled look in step with whatever value we just set.
    var qi = input.closest('quantity-input');
    if (qi && typeof qi.validateQtyRules === 'function') qi.validateQtyRules();
  }

  function validateInner(input, final, quiet) {
    var refs = getRefs(input);
    var lim = limits(input);

    // Sold out outright (stock 0, nothing of it in the cart): not ours to handle. Add to Cart is
    // already disabled server-side as "Sold out", and flagging it data-qty-blocked here would
    // make assets/product-gallery-dots.js treat it as "all in cart" and never open notify-me.
    if (lim.max !== null && lim.max <= 0 && lim.cart <= 0) {
      input.readOnly = false;
      setSubmitBlocked(refs, false);
      hideMessage(refs);
      return;
    }

    // Nothing left to add: every available unit is already in the cart.
    if (lim.max !== null && lim.max <= 0) {
      input.value = 0;
      input.readOnly = true;
      setSubmitBlocked(refs, true);
      // Always explained, even on load / variant swap: a locked field with no reason is confusing.
      showMessage(refs, 'all-in-cart', 0, lim.cart);
      return;
    }
    input.readOnly = false;
    setSubmitBlocked(refs, false);

    var value = parseInt(input.value, 10);
    var floor = Math.max(1, lim.min);

    if (!isNaN(value) && lim.max !== null && value > lim.max) {
      input.value = lim.max;
      var key = lim.cart > 0 ? 'limit-cart' : 'limit';
      if (!quiet) showMessage(refs, key, lim.max, lim.cart);
      return;
    }

    if (final && (isNaN(value) || value < floor)) {
      input.value = floor;
    }

    // A valid quantity clears any earlier explanation.
    if (!isNaN(value) && (lim.max === null || value <= lim.max)) hideMessage(refs);
  }

  function isQuantityInput(target) {
    return target && target.closest && target.closest('.product-form__quantity .quantity__input, [id^="Quantity-"].quantity__input');
  }

  // Keys that can never be part of a valid quantity: minus, plus, "e", decimal separators.
  document.addEventListener('keydown', function (event) {
    var input = isQuantityInput(event.target);
    if (!input) return;
    if (['-', '+', 'e', 'E', '.', ','].indexOf(event.key) !== -1) event.preventDefault();
  });

  document.addEventListener('input', function (event) {
    var input = isQuantityInput(event.target);
    if (!input) return;
    sanitizeDigits(input);
    validate(input, false);
  });

  document.addEventListener('change', function (event) {
    var input = isQuantityInput(event.target);
    if (!input) return;
    validate(input, true);
  });

  document.addEventListener('focusout', function (event) {
    var input = isQuantityInput(event.target);
    if (!input) return;
    validate(input, true);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.quantity__button');
    if (!button) return;
    var input = button.closest('quantity-input');
    input = input && input.querySelector('.quantity__input');
    if (input && isQuantityInput(input)) {
      window.requestAnimationFrame(function () {
        validate(input, true);
      });
    }
  });

  // Variant swaps reset quantity boundaries via assets/product-info.js's setQuantityBoundries(),
  // which publishes this same event (see assets/constants.js) after writing the new min/max/value
  // — revalidate right after, without a message (nothing was typed), so a leftover state from the
  // previous variant never survives a color or size change, and a fully-in-cart variant locks.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.quantityUpdate, function () {
      document.querySelectorAll('.quantity__input').forEach(function (input) {
        if (isQuantityInput(input)) validate(input, true, true);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.quantity__input').forEach(function (input) {
      if (isQuantityInput(input)) validate(input, true, true);
    });
  });
})();
